import React, { useState, useEffect } from 'react';
import { MapPin, Truck, Clock, CheckCircle, Package, Search, Phone, Navigation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons
const driverIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
      <circle cx="20" cy="20" r="18" fill="#3b82f6" stroke="#1e40af" stroke-width="2"/>
      <path d="M12 26 L20 12 L28 26 Z" fill="white"/>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

const pickupIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 40" width="32" height="40">
      <path d="M16 0 C24 0 32 8 32 16 C32 28 16 40 16 40 C16 40 0 28 0 16 C0 8 8 0 16 0 Z" fill="#22c55e" stroke="#166534" stroke-width="2"/>
      <circle cx="16" cy="14" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -40],
});

const deliveryIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 40" width="32" height="40">
      <path d="M16 0 C24 0 32 8 32 16 C32 28 16 40 16 40 C16 40 0 28 0 16 C0 8 8 0 16 0 Z" fill="#ef4444" stroke="#b91c1c" stroke-width="2"/>
      <circle cx="16" cy="14" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -40],
});

const Tracking = () => {
  const [searchParams] = useSearchParams();
  const [trackingNumber, setTrackingNumber] = useState(searchParams.get('number') || '');
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    const preset = searchParams.get('number');
    if (preset) handleTrack(preset);
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh && shipment) {
      interval = setInterval(() => {
        handleTrack(trackingNumber);
      }, 15000); // Refresh every 15 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh, shipment, trackingNumber]);

  const handleTrack = async (trackNum) => {
    if (!trackNum) {
      setError('Please enter a tracking number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tracking/${trackNum}`);
      if (response.data.success) {
        setShipment(response.data.shipment);
        setAutoRefresh(true);
      } else {
        setError('Shipment not found');
        setShipment(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Shipment not found. Please check your tracking number.');
      setShipment(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusStep = (status) => {
    const steps = ['pending', 'accepted', 'in_transit', 'delivered'];
    return steps.indexOf(status) + 1;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-600',
      offered: 'text-blue-600',
      accepted: 'text-blue-600',
      in_transit: 'text-purple-600',
      delivered: 'text-green-600',
    };
    return colors[status] || 'text-gray-600';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  // Calculate map bounds if we have locations
  const getMapCenter = () => {
    if (shipment?.driver_location) {
      return [shipment.driver_location.lat, shipment.driver_location.lng];
    }
    if (shipment?.pickup_location) {
      return [shipment.pickup_location.latitude, shipment.pickup_location.longitude];
    }
    return [47.4762, -122.2603]; // Default Tukwila, WA
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 pt-24 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">Track Your Shipment</h1>
          <p className="text-slate-400 text-lg">Enter your tracking number to see real-time delivery status</p>
        </div>

        {/* Search Box */}
        <Card className="max-w-2xl mx-auto p-6 bg-white/10 backdrop-blur-lg border-white/20 mb-8">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && handleTrack(trackingNumber)}
                placeholder="Enter tracking number (e.g., VL1437-TEST)"
                className="pl-10 h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
              />
            </div>
            <Button 
              onClick={() => handleTrack(trackingNumber)}
              disabled={loading}
              className="h-12 px-8 bg-blue-600 hover:bg-blue-500"
            >
              {loading ? 'Tracking...' : 'Track'}
            </Button>
          </div>
          {error && (
            <div className="mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}
        </Card>

        {/* Results */}
        {shipment && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Card */}
            <Card className="p-6 bg-white">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-sm text-slate-500">Tracking Number</div>
                  <div className="text-xl font-bold font-mono">{shipment.tracking_number || shipment.job_number}</div>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                  shipment.status === 'delivered' ? 'bg-green-100 text-green-800' :
                  shipment.status === 'in_transit' ? 'bg-purple-100 text-purple-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {shipment.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              {/* Progress Steps */}
              <div className="relative mb-8">
                <div className="flex justify-between items-center">
                  {['Order Placed', 'Picked Up', 'In Transit', 'Delivered'].map((step, idx) => {
                    const currentStep = getStatusStep(shipment.status);
                    const isActive = idx < currentStep;
                    const isCurrent = idx === currentStep - 1;
                    
                    return (
                      <div key={step} className="flex flex-col items-center relative z-10">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                          isActive ? 'bg-green-500 border-green-500 text-white' :
                          isCurrent ? 'bg-blue-500 border-blue-500 text-white animate-pulse' :
                          'bg-slate-100 border-slate-300 text-slate-400'
                        }`}>
                          {isActive ? <CheckCircle className="h-5 w-5" /> : idx + 1}
                        </div>
                        <span className={`text-xs mt-2 ${isActive || isCurrent ? 'text-slate-900 font-semibold' : 'text-slate-400'}`}>
                          {step}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {/* Progress Line */}
                <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200 -z-0">
                  <div 
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${((getStatusStep(shipment.status) - 1) / 3) * 100}%` }}
                  />
                </div>
              </div>

              {/* Locations */}
              <div className="space-y-4">
                <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                  <MapPin className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-green-800">Pickup Location</div>
                    <div className="text-sm text-green-700">
                      {shipment.pickup_location?.address}, {shipment.pickup_location?.city}, {shipment.pickup_location?.state}
                    </div>
                    {shipment.picked_up_at && (
                      <div className="text-xs text-green-600 mt-1">
                        Picked up: {formatDate(shipment.picked_up_at)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
                  <MapPin className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-red-800">Delivery Location</div>
                    <div className="text-sm text-red-700">
                      {shipment.delivery_location?.address}, {shipment.delivery_location?.city}, {shipment.delivery_location?.state}
                    </div>
                    {shipment.delivered_at && (
                      <div className="text-xs text-red-600 mt-1">
                        Delivered: {formatDate(shipment.delivered_at)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Driver Info */}
              {shipment.driver_info && shipment.status !== 'delivered' && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                        <Truck className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">{shipment.driver_info.name}</div>
                        <div className="text-sm text-slate-600">{shipment.driver_info.vehicle}</div>
                      </div>
                    </div>
                    {shipment.driver_info.phone && (
                      <a 
                        href={`tel:${shipment.driver_info.phone}`}
                        className="p-3 bg-blue-600 rounded-full text-white hover:bg-blue-500"
                      >
                        <Phone className="h-5 w-5" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Auto-refresh indicator */}
              {autoRefresh && shipment.status !== 'delivered' && (
                <div className="mt-4 text-center text-xs text-slate-500">
                  <span className="inline-flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                    Live tracking - refreshes every 15 seconds
                  </span>
                </div>
              )}
            </Card>

            {/* Map Card */}
            <Card className="p-0 overflow-hidden bg-white" style={{ height: '500px' }}>
              <MapContainer 
                center={getMapCenter()} 
                zoom={10} 
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {/* Pickup Marker */}
                {shipment.pickup_location && (
                  <Marker 
                    position={[shipment.pickup_location.latitude, shipment.pickup_location.longitude]}
                    icon={pickupIcon}
                  >
                    <Popup>
                      <div className="text-center">
                        <div className="font-bold text-green-600">Pickup</div>
                        <div className="text-sm">{shipment.pickup_location.address}</div>
                      </div>
                    </Popup>
                  </Marker>
                )}
                
                {/* Delivery Marker */}
                {shipment.delivery_location && (
                  <Marker 
                    position={[shipment.delivery_location.latitude, shipment.delivery_location.longitude]}
                    icon={deliveryIcon}
                  >
                    <Popup>
                      <div className="text-center">
                        <div className="font-bold text-red-600">Delivery</div>
                        <div className="text-sm">{shipment.delivery_location.address}</div>
                      </div>
                    </Popup>
                  </Marker>
                )}
                
                {/* Driver Marker */}
                {shipment.driver_location && shipment.status !== 'delivered' && (
                  <Marker 
                    position={[shipment.driver_location.lat, shipment.driver_location.lng]}
                    icon={driverIcon}
                  >
                    <Popup>
                      <div className="text-center">
                        <div className="font-bold text-blue-600">Driver Location</div>
                        <div className="text-sm">{shipment.driver_info?.name || 'Your Driver'}</div>
                        <div className="text-xs text-slate-500">
                          Last updated: {formatDate(shipment.driver_location.timestamp)}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                )}
                
                {/* Route Line */}
                {shipment.pickup_location && shipment.delivery_location && (
                  <Polyline
                    positions={[
                      [shipment.pickup_location.latitude, shipment.pickup_location.longitude],
                      ...(shipment.driver_location ? [[shipment.driver_location.lat, shipment.driver_location.lng]] : []),
                      [shipment.delivery_location.latitude, shipment.delivery_location.longitude]
                    ]}
                    color="#3b82f6"
                    weight={3}
                    dashArray="10, 10"
                    opacity={0.7}
                  />
                )}
              </MapContainer>
            </Card>
          </div>
        )}

        {/* No results placeholder */}
        {!shipment && !loading && !error && (
          <Card className="max-w-2xl mx-auto p-12 bg-white/5 border-white/10 text-center">
            <Truck className="h-16 w-16 mx-auto mb-4 text-slate-500" />
            <h3 className="text-xl font-semibold text-white mb-2">Track Your Delivery</h3>
            <p className="text-slate-400">
              Enter your tracking number above to see real-time location updates, delivery status, and driver information.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Tracking;
